import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { GuestState, GuestCheckoutData, Order, OrderTrackingRequest } from '../../types';

const initialState: GuestState = {
  guestInfo: null,
  shippingAddress: null,
  paymentMethod: null,
  loading: false,
  error: null,
};

// Async thunks for guest checkout
export const processGuestCheckout = createAsyncThunk(
  'guest/processCheckout',
  async (checkoutData: GuestCheckoutData, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/v1/checkout/guest', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(checkoutData),
      });

      if (!response.ok) {
        throw new Error('Failed to process guest checkout');
      }

      const order: Order = await response.json();
      return order;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error occurred');
    }
  }
);

export const trackGuestOrder = createAsyncThunk(
  'guest/trackOrder',
  async (trackingData: OrderTrackingRequest, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/v1/orders/track', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(trackingData),
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Order not found');
        }
        throw new Error('Failed to track order');
      }

      const order: Order = await response.json();
      return order;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error occurred');
    }
  }
);

const guestSlice = createSlice({
  name: 'guest',
  initialState,
  reducers: {
    setGuestInfo: (state, action: PayloadAction<{ name: string; email: string; phone: string }>) => {
      state.guestInfo = action.payload;
      // Persist to session storage
      sessionStorage.setItem('guestInfo', JSON.stringify(action.payload));
    },
    setShippingAddress: (state, action) => {
      state.shippingAddress = action.payload;
      // Persist to session storage
      sessionStorage.setItem('guestShippingAddress', JSON.stringify(action.payload));
    },
    setPaymentMethod: (state, action) => {
      state.paymentMethod = action.payload;
      // Persist to session storage
      sessionStorage.setItem('guestPaymentMethod', JSON.stringify(action.payload));
    },
    clearGuestData: (state) => {
      state.guestInfo = null;
      state.shippingAddress = null;
      state.paymentMethod = null;
      state.error = null;
      // Clear from session storage
      sessionStorage.removeItem('guestInfo');
      sessionStorage.removeItem('guestShippingAddress');
      sessionStorage.removeItem('guestPaymentMethod');
    },
    loadGuestDataFromStorage: (state) => {
      // Load guest data from session storage
      const guestInfo = sessionStorage.getItem('guestInfo');
      const shippingAddress = sessionStorage.getItem('guestShippingAddress');
      const paymentMethod = sessionStorage.getItem('guestPaymentMethod');

      if (guestInfo) {
        state.guestInfo = JSON.parse(guestInfo);
      }
      if (shippingAddress) {
        state.shippingAddress = JSON.parse(shippingAddress);
      }
      if (paymentMethod) {
        state.paymentMethod = JSON.parse(paymentMethod);
      }
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Process guest checkout
      .addCase(processGuestCheckout.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(processGuestCheckout.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Clear guest data after successful checkout
        state.guestInfo = null;
        state.shippingAddress = null;
        state.paymentMethod = null;
        // Clear from session storage
        sessionStorage.removeItem('guestInfo');
        sessionStorage.removeItem('guestShippingAddress');
        sessionStorage.removeItem('guestPaymentMethod');
        sessionStorage.removeItem('guestCart');
      })
      .addCase(processGuestCheckout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Track guest order
      .addCase(trackGuestOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(trackGuestOrder.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(trackGuestOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setGuestInfo,
  setShippingAddress,
  setPaymentMethod,
  clearGuestData,
  loadGuestDataFromStorage,
  clearError,
} = guestSlice.actions;

export default guestSlice.reducer;